import { Check, Loader2 } from "lucide-react";

import { Switch } from "@/toggle-ui-kit/control/switch";
import { logIxdEditorAction } from "@/utils/usage-log";
import { useCompareTarget } from "./compare-target-context";
import {
  useIsWorkbookPlanRefreshing,
  useToggleWorkbookAutoSum,
} from "./use-workbook-auto-sum";

/**
 * 워크북 표의 합계 자동계산 스위치 — 매칭 뷰의 "당기" 행 오른쪽 끝에 둔다.
 *
 * 갱신할 캐시는 화면이 내려준 비교 대상(CompareTargetProvider)으로 짚는다 —
 * 화면이 구독 중인 preview/plan 과 같은 키여야 표가 그 자리에서 갱신된다.
 *
 * 저장 뒤 값 반영 방식(plan)을 다시 받는 동안은 토글을 잠근다 — 무거운 요청을
 * 연타로 겹치게 하지 않기 위해서다. 잠긴 이유를 라벨 왼쪽의 "반영 중..."으로 알린다.
 * 이전에는 값 반영 버튼의 스피너가 그 역할이었는데 비활성 상태(24%)에서 흐려져
 * 눈에 띄지 않아 사용자가 "토글이 안 눌린다"는 버그로 읽었다(WXBRL-2664).
 *
 * 갱신 중 판정은 이 컴포넌트의 mutation.isPending 이 아니라 값 반영 버튼과 같은
 * useIsWorkbookPlanRefreshing 으로 한다. 비교 행은 화면을 벗어나면 unmount되므로
 * (useVirtualizedCompareRow) 저장 중에 스크롤로 행이 버려졌다 다시 그려지면
 * 인스턴스에 묶인 isPending 은 false 가 되어, plan GET 이 아직 날아가는 중인데
 * 토글은 풀리고 버튼 툴팁만 "다시 받고 있습니다"로 남는 어긋남이 생긴다.
 */
export const WorkbookAutoSumToggle = ({
  tableId,
  roleKey,
  useAutoSum,
  transpose,
}: {
  tableId: string;
  roleKey: string;
  useAutoSum: boolean;
  /** 매칭된 IXD 표의 방향 — 없으면 비전치 */
  transpose: boolean;
}) => {
  const { workbookId, reportId } = useCompareTarget();
  const { mutate } = useToggleWorkbookAutoSum({ workbookId, reportId });
  const isPending = useIsWorkbookPlanRefreshing(tableId, roleKey);

  return (
    <div className="flex shrink-0 items-center gap-2">
      <span role="status" className="flex shrink-0 items-center gap-1">
        {isPending && (
          <>
            <Loader2 className="size-3.5 animate-spin text-label-alternative" />
            <span className="label-2-medium text-label-alternative">
              반영 중...
            </span>
          </>
        )}
      </span>
      <span className="label-2-medium text-label-normal">합계 자동계산</span>
      <Switch
        size={24}
        color="blue"
        active={useAutoSum}
        disabled={isPending}
        aria-busy={isPending}
        onChange={(next) => {
          logIxdEditorAction(
            next ? "합계 자동계산 켜기" : "합계 자동계산 끄기",
            {
              workbookTableId: tableId,
            },
          );
          mutate({ tableId, roleKey, useAutoSum: next, transpose });
        }}
        aria-label="합계 자동계산"
        icon={
          useAutoSum ? (
            <Check className="size-full text-primary-color" strokeWidth={3.5} />
          ) : undefined
        }
      />
    </div>
  );
};
